import { gql } from '@apollo/client';
import * as Apollo from '@apollo/client';
const lazyLoadGql = (typeof window !== 'undefined') && (window?.blink?.performanceFlags?.isLazyLoadGqlEnabled === 'true');
export function lazyGql(factory) {
    let cached = null;
    if (!lazyLoadGql) {
        return factory();
    }
    return new Proxy({}, {
        get(_target, prop) {
            if (cached === null) {
                cached = factory();
            }
            return cached[prop];
        },
        set(_target, prop, value) {
            if (cached === null) {
                cached = factory();
            }
            cached[prop] = value;
            return true;
        },
        has(_target, prop) {
            if (cached === null) {
                cached = factory();
            }
            return prop in cached;
        },
        ownKeys() {
            if (cached === null) {
                cached = factory();
            }
            return Reflect.ownKeys(cached);
        },
        getOwnPropertyDescriptor(_target, prop) {
            if (cached === null) {
                cached = factory();
            }
            return Object.getOwnPropertyDescriptor(cached, prop);
        },
        getPrototypeOf() {
            if (cached === null) {
                cached = factory();
            }
            return Object.getPrototypeOf(cached);
        },
        setPrototypeOf(_target, proto) {
            if (cached === null) {
                cached = factory();
            }
            return Object.setPrototypeOf(cached, proto);
        },
        defineProperty(_target, prop, descriptor) {
            if (cached === null) {
                cached = factory();
            }
            return Object.defineProperty(cached, prop, descriptor);
        },
        deleteProperty(_target, prop) {
            if (cached === null) {
                cached = factory();
            }
            return delete cached[prop];
        },
        isExtensible() {
            if (cached === null) {
                cached = factory();
            }
            return Object.isExtensible(cached);
        },
        preventExtensions() {
            if (cached === null) {
                cached = factory();
            }
            Object.preventExtensions(cached);
            return true;
        },
        apply(_target, thisArg, argumentsList) {
            if (cached === null) {
                cached = factory();
            }
            return cached.apply(thisArg, argumentsList);
        },
        construct(_target, argumentsList, newTarget) {
            if (cached === null) {
                cached = factory();
            }
            return Reflect.construct(cached, argumentsList, newTarget);
        }
    });
}
const defaultOptions = {};
export var CommunicationConnectionType;
(function (CommunicationConnectionType) {
    CommunicationConnectionType["Msteams"] = "msteams";
    CommunicationConnectionType["Slack"] = "slack";
})(CommunicationConnectionType || (CommunicationConnectionType = {}));
export var CommunicationMessageState;
(function (CommunicationMessageState) {
    CommunicationMessageState["Read"] = "read";
    CommunicationMessageState["Unread"] = "unread";
    CommunicationMessageState["Unseen"] = "unseen";
})(CommunicationMessageState || (CommunicationMessageState = {}));
export var CommunicationMessageType;
(function (CommunicationMessageType) {
    CommunicationMessageType["RequestAccess"] = "requestAccess";
    CommunicationMessageType["Share"] = "share";
})(CommunicationMessageType || (CommunicationMessageType = {}));
export const GetCommunicationPreferencesDocument = lazyGql(() => gql `
    query GetCommunicationPreferences($legacyNotifyOnShare: Boolean) {
  communicationPreferences(legacyNotifyOnShare: $legacyNotifyOnShare) {
    id
    displayName
    channelPreferences {
      id
      displayName
      selected
    }
  }
}
    `);
export function useGetCommunicationPreferencesQuery(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useQuery(GetCommunicationPreferencesDocument, options);
}
export function useGetCommunicationPreferencesLazyQuery(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useLazyQuery(GetCommunicationPreferencesDocument, options);
}
export const UpdateCommunicationPreferencesDocument = lazyGql(() => gql `
    mutation UpdateCommunicationPreferences($update: [CommunicationPreferenceUpdate!]!) {
  updateCommunicationPreferences(update: $update) {
    id
    displayName
    channelPreferences {
      id
      displayName
      selected
    }
  }
}
    `);
export function useUpdateCommunicationPreferencesMutation(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useMutation(UpdateCommunicationPreferencesDocument, options);
}
export const GetMessagesDocument = lazyGql(() => gql `
    query GetMessages($next: String!, $limit: Int!) {
  communicationMessages(next: $next, limit: $limit) {
    items {
      body
      createdAt
      header
      id
      senderId
      state
      cta
      senderName
      type
    }
    lastKey
  }
}
    `);
export function useGetMessagesQuery(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useQuery(GetMessagesDocument, options);
}
export function useGetMessagesLazyQuery(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useLazyQuery(GetMessagesDocument, options);
}
export const GetMessagesMetadataDocument = lazyGql(() => gql `
    query GetMessagesMetadata {
  communicationMessagesMetadata {
    unseenCount
  }
}
    `);
export function useGetMessagesMetadataQuery(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useQuery(GetMessagesMetadataDocument, options);
}
export function useGetMessagesMetadataLazyQuery(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useLazyQuery(GetMessagesMetadataDocument, options);
}
export const UpdateCommunicationMessageDocument = lazyGql(() => gql `
    mutation UpdateCommunicationMessage($update: CommunicationMessageUpdate!) {
  updateCommunicationMessage(update: $update) {
    state
  }
}
    `);
export function useUpdateCommunicationMessageMutation(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useMutation(UpdateCommunicationMessageDocument, options);
}
export const UpdateCommunicationMessagesDocument = lazyGql(() => gql `
    mutation UpdateCommunicationMessages($update: CommunicationMessagesUpdate!) {
  updateCommunicationMessages(update: $update) {
    unseenCount
  }
}
    `);
export function useUpdateCommunicationMessagesMutation(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useMutation(UpdateCommunicationMessagesDocument, options);
}
export const GetCommunicationConnectionsDocument = lazyGql(() => gql `
    query GetCommunicationConnections {
  communicationConnections {
    type
    createdAt
    id
    name
  }
}
    `);
export function useGetCommunicationConnectionsQuery(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useQuery(GetCommunicationConnectionsDocument, options);
}
export function useGetCommunicationConnectionsLazyQuery(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useLazyQuery(GetCommunicationConnectionsDocument, options);
}
export const BuildCommunicationConnectionDocument = lazyGql(() => gql `
    mutation BuildCommunicationConnection($request: CommunicationConnectionRequest!) {
  buildCommunicationConnection(request: $request) {
    redirectUrl
  }
}
    `);
export function useBuildCommunicationConnectionMutation(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useMutation(BuildCommunicationConnectionDocument, options);
}
export const DeleteCommunicationConnectionDocument = lazyGql(() => gql `
    mutation DeleteCommunicationConnection($id: String!) {
  deleteCommunicationConnection(id: $id)
}
    `);
export function useDeleteCommunicationConnectionMutation(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useMutation(DeleteCommunicationConnectionDocument, options);
}
export const GetChannelsDocument = lazyGql(() => gql `
    query GetChannels {
  channels {
    platforms {
      type
      connected
      id
      channels {
        id
        name
      }
    }
  }
}
    `);
export function useGetChannelsQuery(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useQuery(GetChannelsDocument, options);
}
export function useGetChannelsLazyQuery(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useLazyQuery(GetChannelsDocument, options);
}
export const GetUserAccessibleOrgsByParamsDocument = lazyGql(() => gql `
    query getUserAccessibleOrgsByParams($inputData: AccessibleOrgsInput!) {
  getUserAccessibleOrgsByParams(inputData: $inputData) {
    Data {
      orgs {
        orgId
        orgName
        isActive
        nonEmbedAccess
      }
    }
    Error
  }
}
    `);
export function useGetUserAccessibleOrgsByParamsQuery(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useQuery(GetUserAccessibleOrgsByParamsDocument, options);
}
export function useGetUserAccessibleOrgsByParamsLazyQuery(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useLazyQuery(GetUserAccessibleOrgsByParamsDocument, options);
}
export const HasProfilePicDocument = lazyGql(() => gql `
    query HasProfilePic($userId: String) {
  hasProfilePic(userId: $userId)
}
    `);
export function useHasProfilePicQuery(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useQuery(HasProfilePicDocument, options);
}
export function useHasProfilePicLazyQuery(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useLazyQuery(HasProfilePicDocument, options);
}
export const DoLogoutDocument = lazyGql(() => gql `
    mutation DoLogout {
  Session__doLogout {
    Data
    Error
  }
}
    `);
export function useDoLogoutMutation(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useMutation(DoLogoutDocument, options);
}
export const UpdateIsSlackInitiallyConnectedByUserDocument = lazyGql(() => gql `
    mutation UpdateIsSlackInitiallyConnectedByUser($isSlackInitiallyConnectedByUser: Boolean) {
  Session__updateIsSlackInitiallyConnectedByUser(
    isSlackInitiallyConnectedByUser: $isSlackInitiallyConnectedByUser
  )
}
    `);
export function useUpdateIsSlackInitiallyConnectedByUserMutation(baseOptions) {
    const options = { ...defaultOptions, ...baseOptions };
    return Apollo.useMutation(UpdateIsSlackInitiallyConnectedByUserDocument, options);
}
//# sourceMappingURL=graphql-types.js.map